/**
 * The plan drawing, reduced to plain data for the PDF.
 *
 * Shares `buildPlanScene` with the on-screen view rather than re-deriving the
 * geometry, so the printed drawing and the surface the user dragged on cannot
 * show different saunas. The PDF module then knows nothing about the interior
 * domain — it receives rectangles and a scale.
 */
import { getRuleValue } from "../../../data/ruleRegistry";
import {
	deriveInteriorLayoutContext,
	resolveInteriorLayout,
} from "../../../engines/interiorLayoutResolver";
import type { BuildInput } from "../../../engines/types";
import type {
	PlanDrawingData,
	PlanDrawingShape,
} from "../../../lib/exportPlanDrawing";
import { resolvePlanOpenings } from "./planOpenings";
import { buildPlanScene } from "./planScene";

/**
 * Null for engines without a layout-driven interior (the deprecated barrel),
 * and null rather than a throw for anything else that goes wrong.
 *
 * The plan page is an EXTRA in the PDF. Letting this function throw takes the
 * whole export down with it — the material list, the cut list and the build
 * guide included — which is a catastrophically worse outcome than a missing
 * drawing. It is called with whatever `buildInput` the page currently holds,
 * and the interior resolver reaches deep into that object (`thermalInputs`,
 * `roofConfig`, the two-room config), so a partially-built input during a
 * transition is a real possibility rather than a defensive fiction.
 */
export function buildPlanDrawingData(
	buildInput: BuildInput,
): PlanDrawingData | null {
	try {
		return derivePlanDrawingData(buildInput);
	} catch (error) {
		// Swallowed so the export survives, but never silently: a swallowed
		// throw that nobody can see is how a permanently missing PDF page goes
		// unnoticed. The PDF still renders without the plan sheet.
		console.warn(
			"[planDrawing] skipped the plan sheet; the rest of the PDF is unaffected",
			error,
		);
		return null;
	}
}

function derivePlanDrawingData(buildInput: BuildInput): PlanDrawingData | null {
	const context = deriveInteriorLayoutContext(buildInput);
	if (!context) return null;
	const layout = resolveInteriorLayout(buildInput, context);
	if (!layout) return null;

	const halfWidthMm = (buildInput.dimensions.width / 2) * 1000;
	const halfLengthMm = (buildInput.dimensions.length / 2) * 1000;
	// Derived, not declared — the same single owner the on-screen view uses.
	// A literal here was a second copy of a number the engine already computes,
	// and it was the wrong number (95 against the real 138.5).
	const wallThicknessMm = Math.max(
		0,
		Math.min(
			halfWidthMm - context.envelope.maxXMm,
			halfLengthMm - context.envelope.maxZMm,
		),
	);

	const scene = buildPlanScene({
		layout,
		room: context.room,
		relaxRoom: context.relaxRoom,
		heaterSize: context.heaterSize,
		// The openings the ENGINE frames, not the authored ones: a long-entry
		// two-room's front door is never built, and a panorama wall carries no
		// windows. The printed sheet drew both.
		openings: resolvePlanOpenings(buildInput),
		dimensions: buildInput.dimensions,
		swingDepthMm: getRuleValue<number>(
			"ergonomics.doorSwingClearanceMm",
			buildInput.overrides ?? {},
		),
		wallThicknessMm,
	});

	const shapes: PlanDrawingShape[] = [
		...scene.benches.map(
			(bench): PlanDrawingShape => ({
				kind: "bench",
				footprint: bench.footprint,
			}),
		),
		{ kind: "heater", footprint: scene.heater },
	];
	const inside = (box: PlanDrawingShape["footprint"]) =>
		box.minXMm >= -halfWidthMm &&
		box.maxXMm <= halfWidthMm &&
		box.minZMm >= -halfLengthMm &&
		box.maxZMm <= halfLengthMm;
	for (const opening of scene.openings) {
		// The sheet is fitted to the building. A door that opens out sweeps the
		// ground outside (issue 172), which constrains no lav and has no room on
		// the page, so only an inward sweep is printed.
		if (opening.swing && inside(opening.swing)) {
			shapes.push({ kind: "swing", footprint: opening.swing });
		}
		shapes.push({ kind: "opening", footprint: opening.footprint });
	}

	return {
		rooms: scene.rooms.map((room) => ({
			minXMm: room.minXMm,
			maxXMm: room.maxXMm,
			minZMm: room.minZMm,
			maxZMm: room.maxZMm,
		})),
		envelope: {
			minXMm: -halfWidthMm,
			maxXMm: halfWidthMm,
			minZMm: -halfLengthMm,
			maxZMm: halfLengthMm,
		},
		shapes,
		widthMm: buildInput.dimensions.width * 1000,
		lengthMm: buildInput.dimensions.length * 1000,
	};
}
